import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, Sparkles, Wand2, Trash2, ArrowLeft, ChevronRight, Image as ImageIcon,
  Copy, CheckCircle2, Globe, Palette, LayoutGrid, Phone, Pencil, FolderInput, Search, GripVertical,
} from 'lucide-react';
import LinkList from './website-vision/LinkList';
import AssetUploader from './website-vision/AssetUploader';
import BrandPicker from './website-vision/BrandPicker';
import HandoffShareBar, { ApprovalBadge } from './website-vision/HandoffShareBar';
import CompleteVisionBox from './website-vision/CompleteVisionBox';
import ConceptStudio from './website-vision/ConceptStudio';
import LogoConcepts from './website-vision/LogoConcepts';
import BrandKitPanel from './website-vision/BrandKitPanel';
import VisionDetailsDialog, { CATEGORIES } from './website-vision/VisionDetailsDialog';
import FolderSidebar from './website-vision/FolderSidebar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

const SITE_TYPES = [
  ['service', 'Service business'], ['ecommerce', 'E-commerce / shop'], ['coaching', 'Coaching'],
  ['portfolio', 'Portfolio'], ['nonprofit', 'Nonprofit'], ['booking', 'Booking / appointments'],
  ['membership', 'Membership'], ['course', 'Course / education'], ['restaurant', 'Restaurant / food'],
  ['event', 'Event'], ['blog', 'Blog / content'], ['other', 'Something else'],
];
const FIELD_GROUPS = [
  { title: 'Look & feel', icon: Sparkles, fields: [
    ['vibe_words', 'Vibe in a few words', 'e.g. modern, warm, bold, minimal, playful'],
    ['feeling', 'How should visitors FEEL?', 'e.g. confident they found an expert'],
    ['audience', 'Who is this for?', 'your ideal visitor / customer'],
  ] },
  { title: 'Brand notes', icon: Palette, fields: [
    ['color_direction', 'Color notes', 'must-haves, colors to avoid, mood'],
    ['logo_direction', 'Logo', 'have one? want one? style notes'],
  ] },
  { title: 'Structure', icon: LayoutGrid, fields: [
    ['pages', 'Pages you want', 'e.g. Home, About, Services, Booking, Contact'],
    ['features', 'What should the site DO?', 'e.g. take bookings, sell products, capture leads'],
  ] },
  { title: 'Contact details for the site', icon: Phone, fields: [
    ['contact_name', 'Name / business name', ''],
    ['contact_phone', 'Phone', ''],
    ['contact_email', 'Email', ''],
    ['contact_address', 'Address (optional)', ''],
  ] },
];

function CopyBtn({ text, label, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" data-testid={testid} onClick={() => { navigator.clipboard.writeText(text); setDone(true); toast.success('Copied'); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:border-[#1D4ED8] hover:text-[#1D4ED8]">
      {done ? <CheckCircle2 size={15} className="text-emerald-500" /> : <Copy size={15} />} {label}
    </button>
  );
}

const catLabel = (k) => (CATEGORIES.find(([c]) => c === k) || [])[1] || '';

function VisionEditor({ vision: initial, base, q, onBack, isAdmin, folders }) {
  const [vision, setVision] = useState(initial);
  const [fields, setFields] = useState(initial.fields || {});
  const [sites, setSites] = useState(initial.inspiration_sites || []);
  const [others, setOthers] = useState(initial.other_links || []);
  const [siteType, setSiteType] = useState(initial.site_type || '');
  const [gen, setGen] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [rev, setRev] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const applyServer = (d) => { setVision(d); setFields(d.fields || {}); setSiteType(d.site_type || ''); setRev((n) => n + 1); };
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const save = useCallback(async (patch) => {
    try { const r = await ax.patch(`${BACKEND}${base}/${vision.vision_id}${q}`, patch); setVision(r.data); }
    catch { toast.error('Save failed'); }
  }, [base, q, vision.vision_id]);

  const saveField = (k, v) => { const nf = { ...fields, [k]: v }; setFields(nf); save({ fields: nf }); };
  const saveSites = (next) => { setSites(next); save({ inspiration_sites: next }); };
  const saveOthers = (next) => { setOthers(next); save({ other_links: next }); };

  const prefill = async () => {
    setPrefilling(true);
    try {
      const r = await ax.post(`${BACKEND}${base}/${vision.vision_id}/prefill${q}`);
      applyServer(r.data);
      toast.success(r.data.applied ? `AI filled ${r.data.applied} field(s) from your Jumpstart / Accelerator` : 'Nothing new to pull in');
    } catch (e) { toast.error(e.response?.data?.detail || 'Prefill failed'); }
    finally { setPrefilling(false); }
  };

  const generate = async () => {
    setGen(true);
    const started = Date.now();
    try {
      await ax.post(`${BACKEND}${base}/${vision.vision_id}/generate${q}`);
      pollRef.current = setInterval(async () => {
        if (Date.now() - started > 240000) {
          clearInterval(pollRef.current); setGen(false);
          toast.error('This is taking longer than usual — please try again.');
          return;
        }
        const r = await ax.get(`${BACKEND}${base}/${vision.vision_id}${q}`);
        if (r.data.status === 'ready' && r.data.build_brief) {
          clearInterval(pollRef.current); setVision(r.data); setGen(false); toast.success('Your Build Brief is ready!');
        } else if (r.data.generation_error) {
          clearInterval(pollRef.current); setGen(false); toast.error(r.data.generation_error);
        }
      }, 4000);
    } catch (e) { setGen(false); toast.error(e.response?.data?.detail || 'Could not generate'); }
  };

  const brief = vision.build_brief;

  return (
    <div data-testid="vision-editor">
      <button onClick={onBack} data-testid="vision-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#1D4ED8] mb-4"><ArrowLeft size={16} /> Back</button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Globe size={18} className="text-[#1D4ED8]" />
          <div className="min-w-0">
            <h3 className="text-lg font-black text-[#0A0F1C] truncate" data-testid="vision-title">{vision.title || 'Untitled Vision'}</h3>
            <div className="text-xs text-[#64748B]">{[vision.client_name, vision.business_name, catLabel(vision.category)].filter(Boolean).join(' · ')}</div>
          </div>
          <button type="button" onClick={() => setEditOpen(true)} data-testid="vision-edit-btn" className="ml-1 p-1.5 rounded-lg text-[#94A3B8] hover:text-[#1D4ED8] hover:bg-[#EFF6FF]"><Pencil size={15} /></button>
        </div>
      </div>
      <VisionDetailsDialog open={editOpen} onClose={() => setEditOpen(false)} initial={vision} isAdmin={isAdmin} folders={folders}
        onSubmit={async (body) => { await save({ client_name: body.person_name, business_name: body.business_name, category: body.category, ...(isAdmin ? { folder_id: body.folder_id } : {}) }); setEditOpen(false); toast.success('Saved'); }} />

      <ConceptStudio vision={vision} base={base} q={q} onUpdate={applyServer} onSave={save} isAdmin={isAdmin} />

      <CompleteVisionBox vision={vision} base={base} q={q} value={fields.complete_vision || ''}
        onChange={(v) => setFields((f) => ({ ...f, complete_vision: v }))}
        onSaveText={(v) => saveField('complete_vision', v)}
        onServerUpdate={applyServer} onPrefill={prefill} prefilling={prefilling} />

      {/* Site type */}
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase tracking-wide text-[#64748B] mb-1.5">What kind of site is it?</label>
        <div className="flex flex-wrap gap-2" data-testid="vision-site-types">
          {SITE_TYPES.map(([val, lbl]) => (
            <button key={val} type="button" data-testid={`site-type-${val}`} onClick={() => { setSiteType(val); save({ site_type: val }); }}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${siteType === val ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* Field groups */}
      <div className="grid md:grid-cols-2 gap-4">
        {FIELD_GROUPS.map((g) => {
          const Icon = g.icon;
          return (
            <div key={g.title} className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3"><Icon size={15} className="text-[#1D4ED8]" /><h4 className="font-bold text-sm text-[#0A0F1C]">{g.title}</h4></div>
              <div className="space-y-3">
                {g.fields.map(([key, label, ph]) => (
                  <div key={`${key}-${rev}`}>
                    <label className="block text-xs font-semibold text-[#64748B] mb-1">{label}</label>
                    <textarea rows={2} defaultValue={fields[key] || ''} placeholder={ph} data-testid={`field-${key}`}
                      onBlur={(e) => saveField(key, e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:border-[#1D4ED8] outline-none resize-y" />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Uploads */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4" data-testid="vision-uploads">
        <div className="flex items-center gap-2 mb-3"><ImageIcon size={15} className="text-[#1D4ED8]" /><h4 className="font-bold text-sm text-[#0A0F1C]">Upload your files</h4></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AssetUploader vision={vision} kind="logo" label="Logo" hint="We'll pull your brand colors from it" accept="image/*" base={base} q={q} onChange={setVision} />
          <AssetUploader vision={vision} kind="headshot" label="Headshots / photos" hint="PNG or JPEG" accept="image/png,image/jpeg,image/webp,image/*" base={base} q={q} onChange={setVision} />
          <AssetUploader vision={vision} kind="inspiration" label="Inspiration images" hint="Screenshots, mood boards" accept="image/*" base={base} q={q} onChange={setVision} />
          <AssetUploader vision={vision} kind="document" label="Documents" hint="Word, PDF, text — any file (25MB max)" accept="*/*" base={base} q={q} onChange={setVision} />
        </div>
      </div>

      {!vision.concept && ((vision.logo_concepts || []).length > 0 || (vision.assets || []).some((a) => a.kind === 'logo')) && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4"><LogoConcepts vision={vision} base={base} q={q} name={vision.business_name} onUpdate={applyServer} allowCreate={false} /></div>
      )}

      <BrandKitPanel vision={vision} base={base} q={q} onUpdate={applyServer} />

      <BrandPicker vision={vision} onSave={save} />

      {/* Links */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <LinkList title="Websites you love" hint="We can clone the vibe or build from the idea." links={sites} onChange={saveSites} prefix="insp" />
        <LinkList title="Other helpful links" hint="Socials, Google Drive, Canva, current website…" links={others} onChange={saveOthers} prefix="other" placeholderNote="what it is" />
      </div>

      {/* Generate */}
      <div className="mt-6 flex justify-center">
        <button onClick={generate} disabled={gen} data-testid="vision-generate-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1D4ED8] to-[#F97316] text-white font-bold shadow-lg hover:opacity-95 disabled:opacity-70">
          {gen ? <><Loader2 size={18} className="animate-spin" /> Building your brief & full prompt (about 1–2 min)…</> : <><Wand2 size={18} /> {brief ? 'Regenerate Build Brief' : 'Generate Build Brief'}</>}
        </button>
      </div>

      <HandoffShareBar vision={vision} base={base} q={q} onChange={setVision} />

      {/* Brief output */}
      {brief && (
        <div className="mt-6 space-y-4" data-testid="vision-brief">
          <div className="bg-[#0A0F1C] text-slate-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-sm flex items-center gap-2 text-[#F97316]"><Wand2 size={15} /> Vibe-coding prompt (paste into your builder)</h4>
              <CopyBtn text={brief.prompt} label="Copy prompt" testid="copy-prompt" />
            </div>
            <pre className="whitespace-pre-wrap text-xs leading-relaxed font-mono text-slate-200">{brief.prompt}</pre>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-sm text-[#0A0F1C]">Build Brief</h4>
              <CopyBtn text={brief.markdown} label="Copy brief" testid="copy-brief" />
            </div>
            <pre className="whitespace-pre-wrap text-sm leading-relaxed text-[#1e293b] font-sans">{brief.markdown}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function VisionCard({ v, folders, isAdmin, onOpen, onEdit, onMove, onRemove }) {
  return (
    <div draggable={isAdmin} onDragStart={(e) => { e.dataTransfer.setData('text/vision-id', v.vision_id); e.dataTransfer.effectAllowed = 'move'; }}
      onClick={() => onOpen(v.vision_id)} data-testid={`vision-card-${v.vision_id}`}
      className="group cursor-pointer bg-white border border-[#E2E8F0] rounded-xl p-4 hover:shadow-md hover:border-[#1D4ED8]/40 transition-all flex items-center justify-between gap-3">
      {isAdmin && <GripVertical size={15} className="text-[#CBD5E1] shrink-0 cursor-grab" />}
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#0A0F1C] truncate" data-testid={`vision-card-title-${v.vision_id}`}>{v.title || 'Untitled Vision'}</div>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {v.category && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#C2410C]">{catLabel(v.category)}</span>}
          {v.site_type && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{v.site_type}</span>}
          {isAdmin && v.client_email && <span className="text-[10px] text-[#94A3B8] truncate">{v.client_email}</span>}
          <ApprovalBadge approval={v.approval} />
          {v.build_brief ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600"><CheckCircle2 size={12} /> Brief ready</span> : <span className="text-[10px] text-[#94A3B8]">Draft</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => onEdit(v)} data-testid={`vision-card-edit-${v.vision_id}`} title="Edit details" className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#1D4ED8] hover:bg-[#EFF6FF]"><Pencil size={14} /></button>
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid={`vision-card-move-${v.vision_id}`} title="Move to folder" className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#F97316] hover:bg-[#FFF7ED]"><FolderInput size={14} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Move to folder</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {folders.map((f) => (
                <DropdownMenuItem key={f.folder_id} onClick={() => onMove(v.vision_id, f.folder_id)} data-testid={`move-to-${f.folder_id}`}>
                  {v.folder_id === f.folder_id && <CheckCircle2 size={13} className="mr-1.5 text-[#1D4ED8]" />}{f.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onMove(v.vision_id, null)} data-testid="move-to-unfiled">Unfiled</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <button type="button" onClick={() => onRemove(v.vision_id)} data-testid={`vision-card-delete-${v.vision_id}`} title="Delete" className="p-1.5 rounded-lg text-[#CBD5E1] hover:text-red-500"><Trash2 size={14} /></button>
        <ChevronRight size={16} className="text-[#CBD5E1]" />
      </div>
    </div>
  );
}

export default function WebsiteVisionPanel({ base = '/api/admin/website-vision', q = '?person=self', library = false }) {
  const isAdmin = base.startsWith('/api/admin');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    ax.get(`${BACKEND}${base}${q}`).then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [base, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) ax.get(`${BACKEND}/api/admin/website-vision-folders`).then((r) => setFolders(r.data || [])).catch(() => {}); }, [isAdmin]);

  const open = async (id) => { const r = await ax.get(`${BACKEND}${base}/${id}${q}`); setActive(r.data); };
  const submitDialog = async (body) => {
    try {
      if (dialog.mode === 'create') {
        const r = await ax.post(`${BACKEND}${base}${q}`, { ...body, folder_id: body.folder_id || (library && !['all', 'unfiled'].includes(folder) ? folder : null) });
        setDialog(null); setActive(r.data);
      } else {
        const patch = { client_name: body.person_name, business_name: body.business_name, category: body.category };
        if (isAdmin) patch.folder_id = body.folder_id;
        const r = await ax.patch(`${BACKEND}${base}/${dialog.vision.vision_id}${q}`, patch);
        setList((l) => l.map((v) => (v.vision_id === r.data.vision_id ? { ...v, ...r.data } : v))); setDialog(null); toast.success('Saved');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const move = async (id, folderId) => {
    setList((l) => l.map((v) => (v.vision_id === id ? { ...v, folder_id: folderId } : v)));
    try { await ax.patch(`${BACKEND}${base}/${id}${q}`, { folder_id: folderId }); toast.success(folderId ? `Moved to ${(folders.find((f) => f.folder_id === folderId) || {}).name}` : 'Moved to Unfiled'); }
    catch { toast.error('Move failed'); load(); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this website vision?')) return;
    try { await ax.delete(`${BACKEND}${base}/${id}${q}`); setList((l) => l.filter((v) => v.vision_id !== id)); }
    catch { toast.error('Delete failed'); }
  };

  if (active) return <VisionEditor vision={active} base={base} q={q} isAdmin={isAdmin} folders={folders} onBack={() => { setActive(null); load(); }} />;

  const known = new Set(folders.map((f) => f.folder_id));
  const term = search.trim().toLowerCase();
  const shown = list.filter((v) => (!library || folder === 'all' || (folder === 'unfiled' ? !v.folder_id || !known.has(v.folder_id) : v.folder_id === folder))
    && (!term || [v.title, v.client_name, v.business_name, v.client_email].some((x) => (x || '').toLowerCase().includes(term))));

  const listBody = loading ? (
    <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#1D4ED8]" size={24} /></div>
  ) : (
    <div className="space-y-3">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by person, business or email…" data-testid="vision-search"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E2E8F0] text-sm bg-white focus:border-[#1D4ED8] outline-none" />
      </div>
      {shown.map((v) => <VisionCard key={v.vision_id} v={v} folders={folders} isAdmin={isAdmin} onOpen={open} onEdit={(x) => setDialog({ mode: 'edit', vision: x })} onMove={move} onRemove={remove} />)}
      {shown.length === 0 && <div className="text-sm text-[#94A3B8] text-center py-6 border border-dashed border-[#CBD5E1] rounded-xl" data-testid="vision-empty">No visions here yet.</div>}
      <button onClick={() => setDialog({ mode: 'create' })} data-testid="vision-create-btn"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 border-dashed border-[#CBD5E1] text-[#1D4ED8] font-bold hover:border-[#1D4ED8] hover:bg-[#EFF6FF]">
        <Plus size={16} /> Start a Website Vision
      </button>
    </div>
  );

  return (
    <div data-testid="website-vision-panel">
      {library ? (
        <div className="grid lg:grid-cols-[230px_1fr] gap-5">
          <FolderSidebar folders={folders} setFolders={setFolders} list={list} current={folder} onSelect={setFolder} onMove={move} />
          <div>{listBody}</div>
        </div>
      ) : listBody}
      <VisionDetailsDialog open={!!dialog} creating={dialog?.mode === 'create'} initial={dialog?.vision} isAdmin={isAdmin} folders={folders} allowClientPick={library}
        onClose={() => setDialog(null)} onSubmit={submitDialog} />
    </div>
  );
}
